import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ScenarioSlot, samplePosting } from '../data/demo';
import { DemoBundle, getDemoBundle } from '../data/demoInstruments';
import { resolveInstrument } from '../utils/urlSignal';
import { InstrumentId } from '../types/assessment';
export type DemoMode = 'cohort' | 'single';
export type InviteStatus = 'NOT STARTED' | 'IN PROGRESS' | 'SUBMITTED' | 'AWAITING REVIEW';
interface DemoValue {
  postingUrl: string;
  postingText: string;
  usedSample: boolean;
  candidateCount: number;
  mode: DemoMode;
  instrument: InstrumentId;
  bundle: DemoBundle;
  slots: ScenarioSlot[];
  cvName: string | null;
  pipelineComplete: boolean;
  inviteStatuses: InviteStatus[];
  setPostingUrl: (v: string) => void;
  setPostingText: (v: string) => void;
  useSamplePosting: () => void;
  useLegalSamplePosting: () => void;
  setCandidateCount: (n: number) => void;
  setMode: (m: DemoMode) => void;
  setSlotClass: (slotId: string, klass: ScenarioSlot['klass']) => boolean;
  resetSlots: () => void;
  setCvName: (name: string | null) => void;
  setPipelineComplete: (v: boolean) => void;
  setInviteStatuses: (s: InviteStatus[]) => void;
  offLibrary: boolean;
}
const DemoContext = createContext<DemoValue | null>(null);
export function DemoProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [postingUrl, setPostingUrl] = useState('');
  const [postingText, setPostingText] = useState('');
  const [usedSample, setUsedSample] = useState(false);
  const [candidateCount, setCandidateCount] = useState(3);
  const [mode, setMode] = useState<DemoMode>('cohort');
  const [cvName, setCvName] = useState<string | null>(null);
  const [pipelineComplete, setPipelineComplete] = useState(false);
  const [inviteStatuses, setInviteStatuses] = useState<InviteStatus[]>(['NOT STARTED', 'NOT STARTED', 'NOT STARTED']);

  // The posting selects the instrument; nothing else does.
  const resolved = useMemo(() => resolveInstrument(postingUrl, postingText), [postingUrl, postingText]);
  const instrument: InstrumentId = resolved ?? 'FIN-001';
  const bundle = getDemoBundle(instrument);
  const [slots, setSlots] = useState<ScenarioSlot[]>(bundle.slots);

  // A different instrument brings a different scenario mix with it.
  useEffect(() => {
    setSlots(getDemoBundle(instrument).slots);
  }, [instrument]);
  const useSamplePosting = useCallback(() => {
    setPostingUrl(samplePosting.url);
    setPostingText('');
    setUsedSample(true);
  }, []);
  const useLegalSamplePosting = useCallback(() => {
    setPostingUrl(getDemoBundle('LAW-001').posting.url);
    setPostingText('');
    setUsedSample(true);
  }, []);

  // The clean-case floor is enforced here, not in the UI — the refusal is part of the instrument.
  const setSlotClass = useCallback((slotId: string, klass: ScenarioSlot['klass']) => {
    let accepted = true;
    setSlots(prev => {
      const next = prev.map(s => s.id === slotId ? {
        ...s,
        klass
      } : s);
      const cleanCount = next.filter(s => s.klass === 'CLEAN').length;
      if (cleanCount < 2) {
        accepted = false;
        return prev;
      }
      return next;
    });
    return accepted;
  }, []);
  const resetSlots = useCallback(() => setSlots(getDemoBundle(instrument).slots), [instrument]);
  const offLibrary = useMemo(() => {
    if (usedSample) return false;
    if (!`${postingUrl} ${postingText}`.trim()) return false;
    return resolved === null;
  }, [postingUrl, postingText, usedSample, resolved]);
  const value: DemoValue = {
    postingUrl,
    postingText,
    usedSample,
    candidateCount,
    mode,
    instrument,
    bundle,
    slots,
    cvName,
    pipelineComplete,
    inviteStatuses,
    setPostingUrl,
    setPostingText,
    useSamplePosting,
    useLegalSamplePosting,
    setCandidateCount,
    setMode,
    setSlotClass,
    resetSlots,
    setCvName,
    setPipelineComplete,
    setInviteStatuses,
    offLibrary
  };
  return <DemoContext.Provider value={value} data-id="element-405" data-mp="207p43d3knk1j">{children}</DemoContext.Provider>;
}
export function useDemo() {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error('useDemo must be used inside DemoProvider');
  return ctx;
}